/* eslint jsx-a11y/no-noninteractive-tabindex: ["error", { "roles": ["log"] }] -- Enable keyboard scrolling of the conversation. */
import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { Button, Tooltip , TimelineItem } from '@yaradigitallabs/ahua-react';
import {
  ArrowPathIcon,
  // ArrowDownIcon,
  ChatBubbleLeftRightIcon,
  CodeBracketIcon,
  SparklesIcon,
  XMarkIcon,
  // InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Colors } from '../../../public/GlobalTokens';
import ChatContent from './ChatContent';
import { FORMULA_MATE_PROMPTS } from './formulaMate.constants';
import { useFormulaMate } from './FormulaMateContext';
import {
  formulaMateService,
  FormulaMateEmptyResponseError,
  FormulaMateIntegrationError,
  FormulaMateRequestError,
} from './formulaMateService';
import type {
  FormulaMateMessage,
  FormulaMateRequest,
  FormulaMateService,
  FormulaMateWorkflow,
} from './formulaMate.types';
import './formulaMate.css';

const theme: CSSProperties & Record<`--fm-${string}`, string> = {
  '--fm-brand': Colors.colorType.brand.dark,
  '--fm-brand-dark': Colors.colorType.brand.darkest,
  '--fm-brand-light': Colors.colorType.brand.lightest,
  '--fm-danger': Colors.colorType.destructive.dark,
  '--fm-surface': Colors.colorType.grayscale.lightest,
};

type Failure =
  | { type: 'session'; message: string }
  | { type: 'message'; request: FormulaMateRequest; message: string }
  | { type: 'end'; message: string };

export default function FormulaMate({
  service = formulaMateService,
  visible = true,
}: {
  service?: FormulaMateService;
  visible?: boolean;
}) {
  const id = useId();
  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);
  const { reviewRequest } = useFormulaMate();
  const [open, setOpen] = useState(false);
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [workflow, setWorkflow] = useState<FormulaMateWorkflow>('generation');
  const [prompt, setPrompt] = useState('');
  const [code, setCode] = useState('');
  const [messages, setMessages] = useState<FormulaMateMessage[]>([]);
  const [busy, setBusy] = useState<'message' | 'end' | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const sessionId = useRef<string | null>(null);
  const requestController = useRef<AbortController | null>(null);
  const nextMessageId = useRef(0);
  const launcher = useRef<HTMLButtonElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const conversation = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const lifecycle = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setSessionLoading(true);
    setFailure(null);
    const start = lifecycle.current
      .catch(() => {
        console.warn(
          'Retrying FormulaMate session cleanup after a previous failure.'
        );
      })
      .then(async () => {
        // Retry cleanup before creating another session if deletion previously failed.
        if (sessionId.current) {
          await serviceRef.current.endSession(
            sessionId.current,
            new AbortController().signal
          );
          sessionId.current = null;
        }
        sessionId.current = await serviceRef.current.createSession(
          new AbortController().signal
        );
      });
    lifecycle.current = start;
    void start.then(
      () => {
        if (active) setSessionLoading(false);
      },
      error => {
        if (active) {
          setSessionLoading(false);
          setFailure({
            type: 'session',
            message:
              error instanceof Error
                ? error.message
                : 'Could not start FormulaMate AI. Please retry.',
          });
        }
      }
    );
    return () => {
      active = false;
      requestController.current?.abort();
      requestController.current = null;
      setBusy(null);
      setMessages([]);
      setPrompt('');
      setCode('');
      setWorkflow('generation');
      const cleanup = start
        .catch(() => {
          console.warn(
            'FormulaMate initialization failed before session cleanup.'
          );
        })
        .then(async () => {
          if (sessionId.current) {
            await serviceRef.current.endSession(
              sessionId.current,
              new AbortController().signal
            );
            sessionId.current = null;
          }
        });
      lifecycle.current = cleanup;
      void cleanup.catch(() => {
        console.error(
          'FormulaMate session cleanup failed. Cleanup will be retried before starting another session.'
        );
      });
    };
  }, [visible, sessionAttempt]);

  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  useEffect(
    () => () => {
      requestController.current?.abort();
    },
    []
  );

  useEffect(() => {
    if (open && visible) composer.current?.focus();
  }, [open, workflow, reviewRequest, visible]);

  useEffect(() => {
    if (!reviewRequest) return;
    setCode(reviewRequest.code);
    setWorkflow('review');
    setOpen(true);
    setFailure(null);
    composer.current?.focus();
  }, [reviewRequest]);

  useEffect(() => {
    if (open && nearBottom.current && conversation.current) {
      conversation.current.scrollTop = conversation.current.scrollHeight;
    }
  }, [open, messages, busy, failure]);

  const latest = () => {
    nearBottom.current = true;
    setAtBottom(true);
    if (conversation.current) {
      conversation.current.scrollTop = conversation.current.scrollHeight;
    }
  };

  const errorMessage = (error: unknown, ending = false) =>
    error instanceof FormulaMateIntegrationError ||
    error instanceof FormulaMateEmptyResponseError ||
    error instanceof FormulaMateRequestError
      ? error.message
      : ending
        ? 'We could not end this conversation. Your messages are still here. Please try again.'
        : 'We could not get a response from FormulaMate AI. Please try again. If the request reached the service, retrying may send it again.';

  const send = async (request: FormulaMateRequest, retry = false) => {
    if (requestController.current) return;
    const controller = new AbortController();
    requestController.current = controller;
    setBusy('message');
    setFailure(null);
    latest();

    if (!retry) {
      const blocks: FormulaMateMessage['blocks'] =
        request.workflow === 'generation'
          ? [{ type: 'text', text: request.prompt }]
          : [
              { type: 'code', language: 'python', code: request.code },
              ...(request.instructions.trim()
                ? [{ type: 'text' as const, text: request.instructions }]
                : []),
            ];
      setMessages(previous => [
        ...previous,
        {
          id: nextMessageId.current++,
          role: 'user',
          workflow: request.workflow,
          blocks,
        },
      ]);
      if (request.workflow === 'generation') setPrompt('');
      else {
        setCode('');
      }
    }

    try {
      await lifecycle.current;
      if (!sessionId.current)
        throw new Error('FormulaMate AI has no active session.');
      if (controller.signal.aborted) return;
      const response =
        request.workflow === 'generation'
          ? await service.generateFormula(
              sessionId.current,
              request.prompt,
              controller.signal
            )
          : await service.reviewCode(
              sessionId.current,
              request.code,
              request.instructions,
              controller.signal
            );
      if (controller.signal.aborted) return;
      if (response.sessionId) sessionId.current = response.sessionId;
      setMessages(previous => [
        ...previous,
        {
          id: nextMessageId.current++,
          role: 'assistant',
          workflow: request.workflow,
          blocks: response.blocks,
        },
      ]);
    } catch (error) {
      if (!controller.signal.aborted) {
        setFailure({
          type: 'message',
          request,
          message: errorMessage(error),
        });
      }
    } finally {
      if (!controller.signal.aborted) {
        requestController.current = null;
        setBusy(null);
      }
    }
  };

  const endConversation = async () => {
    if (requestController.current) return;
    const controller = new AbortController();
    requestController.current = controller;
    setBusy('end');
    setFailure(null);
    try {
      if (sessionId.current) {
        await service.endSession(sessionId.current, controller.signal);
      }
      if (controller.signal.aborted) return;
      sessionId.current = null;
      setMessages([]);
      setPrompt('');
      setCode('');
      latest();
      setSessionAttempt(attempt => attempt + 1);
    } catch (error) {
      if (!controller.signal.aborted) {
        setFailure({ type: 'end', message: errorMessage(error, true) });
      }
    } finally {
      if (!controller.signal.aborted) {
        requestController.current = null;
        setBusy(null);
      }
    }
  };

  const submit = () => {
    if (busy || failure || sessionLoading) return;
    if (workflow === 'generation' && prompt.trim()) {
      void send({ workflow, prompt });
    } else if (workflow === 'review' && code.trim()) {
      void send({ workflow, code, instructions: '' });
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  };

  const chooseWorkflow = (next: FormulaMateWorkflow) => {
    setWorkflow(next);
    composer.current?.focus();
  };

  if (!visible) return null;

  return (
    <div id="formulamate-chat" className="fm-root" style={theme}>
      <div ref={setSelectPortal} className="fm-select-portal" />
      {open && (
        <section
          id={`${id}-panel`}
          className="fm-panel"
          aria-labelledby={`${id}-title`}
        >
          <header className="fm-header">
            <Tooltip
              concept="neutral"
              position="bottom"
              maxWidth="min(300px, calc(100vw - 32px))"
              css={{ zIndex: 62, whiteSpace: 'normal', lineHeight: 1.5 }}
              text="Hi! I'm FormulaMate AI. I can help you generate Python formulas for Soft Tag Manager or review existing formulas for potential issues and improvements. What would you like to work on?"
              tipVisibility
            >
              <TimelineItem.IconButton colorConcept="inverse" icon="Info" />
            </Tooltip>
            <div>
              <h2 id={`${id}-title`}>FormulaMate AI</h2>
            </div>
            <Button
              variant="ghost"
              size="xs"
              colorConcept="inverse"
              type="button"
              className="fm-icon-button"
              aria-label="Close FormulaMate AI"
              onClick={() => {
                setOpen(false);
                launcher.current?.focus();
              }}
            >
              <XMarkIcon aria-hidden="true" />
            </Button>
          </header>

          <div
            className="fm-workflows"
            role="group"
            aria-label="Assistant workflow"
          >
            <Button
              variant="ghost"
              size="xs"
              type="button"
              aria-pressed={workflow === 'generation'}
              onClick={() => chooseWorkflow('generation')}
            >
              <SparklesIcon aria-hidden="true" /> Generate a Formula
            </Button>
            <Button
              variant="ghost"
              size="xs"
              type="button"
              aria-pressed={workflow === 'review'}
              onClick={() => chooseWorkflow('review')}
            >
              <CodeBracketIcon aria-hidden="true" /> Review Existing Code
            </Button>
          </div>

          <div
            ref={conversation}
            className="fm-conversation"
            role="log"
            aria-label="FormulaMate conversation"
            aria-live="polite"
            aria-relevant="additions"
            tabIndex={0}
            onScroll={event => {
              const { scrollHeight, scrollTop, clientHeight } =
                event.currentTarget;
              const near = scrollHeight - scrollTop - clientHeight < 48;
              nearBottom.current = near;
              setAtBottom(near);
            }}
          >
            {messages.length === 0 && (
              <div className="fm-welcome">
                <span className="fm-welcome-icon">
                  <SparklesIcon aria-hidden="true" className="mr-1" />
                  {`Let's get started with formula ${workflow === 'generation' ? 'generation' : 'review'}`}
                </span>
              </div>
            )}
            {messages.map(message => (
              <article
                key={message.id}
                className={`fm-message fm-message-${message.role}`}
                aria-label={`${message.role === 'user' ? 'You' : 'FormulaMate AI'}: ${message.workflow === 'generation' ? 'Formula generation' : 'Code review'}`}
              >
                <span className="fm-message-label">
                  {message.role === 'user' ? 'You' : 'FormulaMate AI'}
                  {' · '}
                  {message.workflow === 'generation' ? 'Generation' : 'Review'}
                </span>
                <ChatContent blocks={message.blocks} />
              </article>
            ))}
            {(busy || sessionLoading) && (
              <div className="fm-loading" role="status">
                <ArrowPathIcon aria-hidden="true" />
                {sessionLoading
                  ? 'Starting FormulaMate AI...'
                  : busy === 'end'
                    ? 'Ending conversation...'
                    : 'Waiting for FormulaMate AI...'}
              </div>
            )}
            {failure && (
              <div className="fm-error">
                <p role="alert">{failure.message}</p>
                <div className="fm-error-actions">
                  <Button
                    variant="ghost"
                    size="xs"
                    type="button"
                    onClick={() =>
                      failure.type === 'session'
                        ? setSessionAttempt(attempt => attempt + 1)
                        : failure.type === 'message'
                          ? void send(failure.request, true)
                          : void endConversation()
                    }
                  >
                    <ArrowPathIcon aria-hidden="true" /> Retry
                  </Button>
                  {failure.type !== 'session' && (
                    <Button
                      variant="ghost"
                      size="xs"
                      type="button"
                      onClick={() => {
                        if (failure.type === 'message') {
                          setWorkflow(failure.request.workflow);
                          if (failure.request.workflow === 'generation') {
                            const request = failure.request;
                            setPrompt(current => current || request.prompt);
                          } else {
                            const request = failure.request;
                            setCode(current => current || request.code);
                          }
                        }
                        setFailure(null);
                        composer.current?.focus();
                      }}
                    >
                      Keep editing
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          <form
            className="fm-composer"
            aria-label={
              workflow === 'generation' ? 'Formula generation' : 'Code review'
            }
            onSubmit={event => {
              event.preventDefault();
              submit();
            }}
          >
            {workflow === 'generation' && (
              <div className="fm-field">
                <select
                  value=""
                  onChange={event => {
                    if (event.target.value) {
                      setPrompt(event.target.value);
                      composer.current?.focus();
                    }
                  }}
                >
                  <option value="">Choose a prompt to edit...</option>
                  {FORMULA_MATE_PROMPTS.map(example => (
                    <option key={example} value={example}>
                      {example}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className="fm-field" htmlFor={`${id}-input`}>
              {workflow === 'generation'
                ? 'Describe your calculation'
                : 'Python formula to review'}
            </label>
            <textarea
              ref={composer}
              id={`${id}-input`}
              className={workflow === 'review' ? 'fm-code-input' : ''}
              rows={2}
              value={workflow === 'generation' ? prompt : code}
              placeholder={
                workflow === 'generation'
                  ? 'Describe the calculation you want to perform on your sensor data...'
                  : 'Paste your existing Soft Tag Manager formula here...'
              }
              onChange={event =>
                workflow === 'generation'
                  ? setPrompt(event.target.value)
                  : setCode(event.target.value)
              }
              onKeyDown={onKeyDown}
              aria-describedby={`${id}-hint`}
              spellCheck={workflow !== 'review'}
            />
            <footer className="fm-footer">
              <div className="fm-composer-actions">
                <Button
                  variant="primary"
                  size="xs"
                  type="submit"
                  className="fm-send"
                  iconTrailing="Send"
                  disabled={
                    !!busy ||
                    sessionLoading ||
                    !!failure ||
                    !(workflow === 'generation' ? prompt : code).trim()
                  }
                >
                  Send
                </Button>
              </div>
              <div>
                <span>Review AI suggestions before using them.</span>
                {messages.length > 0 && (
                  <a
                    href={`#${id}-input`}
                    aria-disabled={!!busy}
                    onClick={event => {
                      event.preventDefault();
                      if (!busy) void endConversation();
                    }}
                  >
                    End conversation
                  </a>
                )}
              </div>
            </footer>
          </form>
        </section>
      )}
      <Button
        variant="primary"
        size="s"
        ref={launcher}
        type="button"
        className="fm-launcher"
        aria-label="Open FormulaMate AI"
        aria-expanded={open}
        aria-controls={open ? `${id}-panel` : undefined}
        title="FormulaMate AI"
        onClick={() => {
          setOpen(true);
          composer.current?.focus();
        }}
      >
        <ChatBubbleLeftRightIcon aria-hidden="true" />
      </Button>
    </div>
  );
}
